import React from 'react';
import chunk from 'lodash/chunk';
import { useLazyQuery } from 'apollo-client-ppc';
import { getPurchaseMetadataEntries } from '@atlassian/cssxp-utils-entitlement';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import {
  FetchOrdersByTransactionAccountIdInPpcQuery,
  FetchOrdersByTransactionAccountIdInPpcQueryVariables,
  FilterOption,
  InvoiceGroup,
  Order,
} from '@css-xp/graphtypes/src/generated-types-new';
import { downloadAsCSV } from '@css-xp/ppc-utils';
import { maxThresholdForResults } from '@css-xp/ppc-constants';
import { errorNotificationBuilder, NotificationTypes, successNotificationBuilder, useNotification } from '@css-xp/base';
import { getOrderDetailViewCSVContent } from '@css-xp/base/entitlement-utils';
import { failureMessage } from '@css-xp/ppc-common';
import { fetchOrdersForTransactionAccountInPPC } from '../queries/fetch-orders-for-transaction-account';

export const header = [
  'Order Id',
  'Order Number',
  'Subscription',
  'Plan',
  'Order date',
  'Company',
  'Sold-to address',
  'Site',
  'Order summary',
  'Notes',
  'Support ticket',
  'Placed by',
];

// Safe number of records to fetch in query
const SAFE_CHUNK_SIZE_FOR_ORDER_HISTORY_TABLE_EXPORT = 50;
// Safe number of records to fetch at one go
const SAFE_NUMBER_OF_RECORDS_TO_FETCH_AT_ONE_GO = 500;

export const useOrderHistoryCSV = (
  transactionAccountId: string,
  filterOptions?: FilterOption[] | FilterOption,
  isFlatDiscountSupportEnabled?: boolean
) => {
  const [ordersDataLoading, setOrdersDataLoading] = React.useState(false);
  const client = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const { sendErrorNotification, sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const [loadOrdersData] = useLazyQuery<
    FetchOrdersByTransactionAccountIdInPpcQuery,
    FetchOrdersByTransactionAccountIdInPpcQueryVariables
  >(fetchOrdersForTransactionAccountInPPC, {
    variables: {
      transactionAccountId,
      searchOptions: { first: maxThresholdForResults, offset: 0 },
      filterOptions,
    },
    notifyOnNetworkStatusChange: true,
  });

  const downloadOrdersDataCSV = async () => {
    const { data } = await loadOrdersData();
    const ordersData = data?.transactionAccount?.orders?.nodes;

    const csvData = ordersData?.map((order) =>
      (order?.orderLineItems?.nodes || []).map((item) => {
        const invoiceGroup = data?.transactionAccount?.availableInvoiceGroups?.nodes?.find(
          (invoiceGroup) => invoiceGroup?.id === order?.billingGroup?.billingGroupId
        );
        const shipTo = invoiceGroup?.shipToParty;
        const purchaseMetadata = getPurchaseMetadataEntries(item?.orderLineItemsPurchaseMetadata);
        const address = shipTo?.addressLine1
          ? `${shipTo?.addressLine1}, ${shipTo?.addressLine2}, ${shipTo?.city},${shipTo?.country}, ${shipTo?.state}, ${shipTo?.postcode}`
          : '-';
        const orderSummary = getOrderDetailViewCSVContent({
          orderLineItem: item,
          billTo: order?.billTo,
          isFlatDiscountSupportEnabled,
        });

        return [
          order?.orderNumber ?? '-',
          item?.entitlementNumber ?? '-',
          item?.edition ?? '-',
          order?.created ?? '-',
          invoiceGroup?.shipToParty?.name ?? '-',
          address,
          item?.cloudSiteHostname || '-',
          orderSummary,
          purchaseMetadata?.note || '-',
          order?.metadata?.supportTicketNumber || '-',
          order?.contextSubject || '-',
        ];
      })
    );

    downloadAsCSV({
      fileName: 'orders-history',
      header,
      data: csvData?.flat() || [],
    });
  };

  const downloadOrdersDataCSVv2 = async (totalCount: number) => {
    try {
      setOrdersDataLoading(true);
      if (!notificationSent) {
        const notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Export process started')
          .withMessage(
            `Please remain on this page until the process is finished. We'll notify you once the export is ready.`
          )
          .withDismissAfter(5000)
          .build();
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
      /*
        If there are total 550 records, we will first divide it into 11 chunks of 50 records each and then
        divide it into 3 set of requests to fetch all the records.
        1st request will fetch 5 chunks of 50 records each. Total 250 records and 5 graphql requests.
        2nd request will fetch 5 chunks of 50 records each. Total 250 records and 5 graphql requests.
        3rd request will fetch 1 chunk of 50 records. Total 50 records and 1 graphql request. Total 550 records fetched.
       */
      const numberOfRequests = Math.ceil(totalCount / SAFE_NUMBER_OF_RECORDS_TO_FETCH_AT_ONE_GO);
      const chunks = chunk([...Array(totalCount).keys()], SAFE_CHUNK_SIZE_FOR_ORDER_HISTORY_TABLE_EXPORT);
      const requestChunks = chunk(chunks, numberOfRequests);
      let ordersData: Array<Order> = [];
      let allAvailableInvoiceGroups: Array<InvoiceGroup> = [];
      for (let i = 0; i < requestChunks.length; i++) {
        const promisesOfOrders =
          requestChunks[i]?.map((requestChunk) => {
            const offset = requestChunk[0];
            if (offset < totalCount) {
              return client.query({
                query: fetchOrdersForTransactionAccountInPPC,
                variables: {
                  transactionAccountId,
                  searchOptions: { first: SAFE_CHUNK_SIZE_FOR_ORDER_HISTORY_TABLE_EXPORT, offset },
                  filterOptions,
                },
              });
            }
          }) ?? [];

        const orderResponses = await Promise.all(promisesOfOrders);
        ordersData = [
          ...ordersData,
          ...orderResponses.map((response) => response?.data?.transactionAccount?.orders?.nodes).flat(),
        ];
        allAvailableInvoiceGroups = [
          ...allAvailableInvoiceGroups,
          ...orderResponses.map((response) => response?.data?.transactionAccount?.availableInvoiceGroups?.nodes).flat(),
        ];
      }

      const csvData = ordersData?.map((order) =>
        (order?.orderLineItems?.nodes || []).map((item: any) => {
          // @ts-ignore
          const invoiceGroup = allAvailableInvoiceGroups?.find(
            (invoiceGroup) => invoiceGroup?.id === order?.billingGroup?.billingGroupId
          );
          const shipTo = invoiceGroup?.shipToParty;
          const purchaseMetadata = getPurchaseMetadataEntries(item?.orderLineItemsPurchaseMetadata);
          const address = shipTo?.addressLine1
            ? `${shipTo?.addressLine1}, ${shipTo?.addressLine2}, ${shipTo?.city},${shipTo?.country}, ${shipTo?.state}, ${shipTo?.postcode}`
            : '-';
          const orderSummary = getOrderDetailViewCSVContent({
            orderLineItem: item,
            billTo: order?.billTo,
            isFlatDiscountSupportEnabled,
          });
          return [
            order?.id,
            order?.orderNumber ?? '-',
            item?.entitlementNumber ?? '-',
            item?.edition ?? '-',
            order?.created ?? '-',
            invoiceGroup?.shipToParty?.name ?? '-',
            address,
            item?.cloudSiteHostname || '-',
            orderSummary,
            purchaseMetadata?.note || '-',
            order?.metadata?.supportTicketNumber || '-',
            order?.contextSubject || '-',
          ];
        })
      );

      downloadAsCSV({
        fileName: 'orders-history',
        header,
        data: csvData?.flat() || [],
      });
    } catch (e) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Export unsuccessful.')
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      setOrdersDataLoading(false);
    } finally {
      const notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader('Export successful.')
        .withMessage('The file has been successfully generated.')
        .withDismissAfter(10000)
        .build();
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
      setOrdersDataLoading(false);
    }
  };

  return { downloadOrdersDataCSV, downloadOrdersDataCSVv2, ordersDataLoading };
};
